// ----------------------------------------------------------------------------
// Live admin notifications: pending join requests, assignment responses and
// availability changes. Same sources and lifecycle as the shared
// NotificationBell (SignalR MembershipHub + 30s polling fallback + the
// cross-page membership event bus), lifted into a provider so the header bell
// and the Notifications inbox share one connection.
// ----------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { logger } from '@app/utils/logger';
import { membershipsApi } from '@app/api/organization';
import { getAccessToken } from '@app/api/auth';
import { onMembershipChanged, emitMembershipChanged } from '@app/utils/membershipEvents';
import type { EnrichedMembership } from '@app/types';

const POLL_INTERVAL_MS = 30_000;
const KEEP = 20;

export type LiveNotificationKind = 'request' | 'assignment' | 'availability';

export interface LiveNotification {
  id: string;
  kind: LiveNotificationKind;
  who: string;
  what: string;
  at: string; // ISO timestamp
  /** Routing hints for "open the source". */
  planId?: string;
  groupId?: string;
  read: boolean;
}

interface LiveNotificationsValue {
  pendingMembers: EnrichedMembership[];
  pendingCount: number;
  items: LiveNotification[];
  hasUnread: boolean;
  markAllRead: () => void;
  markRead: (id: string) => void;
  refreshPending: () => Promise<void>;
}

const Ctx = createContext<LiveNotificationsValue | null>(null);

export function LiveNotificationsProvider({
  churchId,
  userId,
  children,
}: {
  churchId: string | null | undefined;
  userId?: string;
  children: ReactNode;
}) {
  const [pendingMembers, setPendingMembers] = useState<EnrichedMembership[]>([]);
  const [events, setEvents] = useState<LiveNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());
  const userRef = useRef(userId);
  userRef.current = userId;

  const refreshPending = useCallback(async () => {
    if (!churchId) return;
    try {
      setPendingMembers(await membershipsApi.getPendingMembers(churchId));
    } catch (error) {
      logger.error('Failed to fetch pending memberships:', error);
    }
  }, [churchId]);

  const push = useCallback((n: Omit<LiveNotification, 'read'>) => {
    setEvents((prev) => [{ ...n, read: false }, ...prev].slice(0, KEEP));
  }, []);

  useEffect(() => {
    if (!churchId) return;
    const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:5005';
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${gatewayUrl}/api/organizations/hubs/membership`, {
        withCredentials: true,
        // Church-scoped events only reach connections the gateway can identify;
        // browsers send this as the access_token query param on the upgrade.
        accessTokenFactory: () => getAccessToken() || '',
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('MembershipRevoked', (revokedChurchId: string) => {
      connection.invoke('LeaveChurch', revokedChurchId).catch((err) => logger.error('SignalR LeaveChurch failed:', err));
    });
    const onRequestChange = (label: string) => (data: { churchId: string }) => {
      logger.info(`Real-time: ${label}`, data);
      if (data.churchId === churchId) void refreshPending();
    };
    connection.on('MembershipRequestReceived', onRequestChange('New membership request received'));
    connection.on('MembershipRequestApproved', onRequestChange('Membership request approved'));
    connection.on('MembershipRequestCanceled', onRequestChange('Membership request canceled'));
    connection.on('MemberLeft', (data: { userId: string; churchId: string; userName: string; reason: string }) => {
      logger.info('Real-time: Member left/removed', data);
      if (data.churchId === churchId) {
        void refreshPending();
        emitMembershipChanged();
      }
    });
    connection.on(
      'AssignmentResponseReceived',
      (data: {
        churchId: string;
        planId: string;
        planTitle: string;
        memberName: string;
        memberRole: string;
        status: string;
        changedByUserId?: string;
        timestamp: string;
      }) => {
        logger.info('Real-time: Assignment response received', data);
        // Skip the admin's own changes.
        if (data.churchId !== churchId || data.changedByUserId === userRef.current) return;
        push({
          id: `${data.planId}-${data.memberName}-${Date.now()}`,
          kind: 'assignment',
          who: data.memberName,
          what: `${(data.status || '').toLowerCase()} ${data.memberRole} for ${data.planTitle}`,
          at: data.timestamp || new Date().toISOString(),
          planId: data.planId,
        });
      },
    );
    connection.on(
      'AvailabilityUpdated',
      (data: {
        churchId: string;
        groupId: string;
        userId: string;
        changedByUserId?: string;
        memberName: string;
        summary: string;
        timestamp: string;
      }) => {
        logger.info('Real-time: Availability updated', data);
        if (data.churchId !== churchId || data.changedByUserId === userRef.current) return;
        push({
          id: `${data.userId}-${Date.now()}`,
          kind: 'availability',
          who: data.memberName,
          what: data.summary,
          at: data.timestamp || new Date().toISOString(),
          groupId: data.groupId,
        });
      },
    );

    // Group membership is per connection, so re-join after every (re)connect.
    const joinChurch = () => connection.invoke('JoinChurch', churchId).catch((err) => logger.error('SignalR JoinChurch failed:', err));
    (async () => {
      try {
        await connection.start();
        logger.info('SignalR connected to MembershipHub');
        await joinChurch();
      } catch (err) {
        logger.error('SignalR connection failed:', err);
      }
      void refreshPending();
    })();
    connection.onreconnected(() => {
      logger.info('SignalR reconnected');
      void joinChurch();
      void refreshPending();
    });
    return () => {
      connection.stop().catch((err) => logger.error('Error stopping SignalR connection:', err));
    };
  }, [churchId, refreshPending, push]);

  // Polling fallback keeps the count fresh if SignalR is down.
  useEffect(() => {
    if (!churchId) return;
    const t = setInterval(refreshPending, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [churchId, refreshPending]);

  // Approvals/denials on other pages.
  useEffect(() => onMembershipChanged(() => void refreshPending()), [refreshPending]);

  const value = useMemo<LiveNotificationsValue>(() => {
    const requestItems: LiveNotification[] = pendingMembers.map((m) => ({
      id: `req-${m.id}`,
      kind: 'request',
      who: m.username || m.email,
      what: 'asked to join your church',
      at: m.joinedAt,
      read: readIds.has(`req-${m.id}`),
    }));
    const items = [...events.map((e) => ({ ...e, read: e.read || readIds.has(e.id) })), ...requestItems].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
    return {
      pendingMembers,
      pendingCount: pendingMembers.length,
      items,
      hasUnread: items.some((i) => !i.read),
      markAllRead: () => setReadIds(new Set(items.map((i) => i.id))),
      markRead: (id: string) => setReadIds((prev) => new Set(prev).add(id)),
      refreshPending,
    };
  }, [pendingMembers, events, readIds, refreshPending]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const EMPTY: LiveNotificationsValue = {
  pendingMembers: [],
  pendingCount: 0,
  items: [],
  hasUnread: false,
  markAllRead: () => {},
  markRead: () => {},
  refreshPending: async () => {},
};

export const useLiveNotifications = () => useContext(Ctx) ?? EMPTY;
